import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "nativewind/jsx-runtime";
import { SccButton } from '@/components/atoms';
import { SafeAreaWrapper } from '@/components/SafeAreaWrapper';
import { MAX_NUMBER_OF_TAKEN_PHOTOS } from '@/constant/constant';
import { font } from '@/constant/font';
import { FloorMovingMethodTypeDto, StairInfo, } from '@/generated-sources/openapi';
import { useKeyboardVisible } from '@/hooks/useKeyboardVisible';
import ToastUtils from '@/utils/ToastUtils';
import { useEffect, useMemo, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Image, Platform, ScrollView, View } from 'react-native';
import styled from 'styled-components/native';
import PlaceInfoSection from '../../PlaceReviewFormScreen/sections/PlaceInfoSection';
import { FORM_TOAST_OPTIONS, formImages, makeFloorMovementOptions, } from '../constants';
import { getElevatorConditions, ELEVATOR_OPTIONS } from '../hooks';
import { Hint, Label, MeasureGuide, OptionsGroup, QuestionSection, QuestionText, SectionLabel, SectionSeparator, SubSection, SubmitButtonWrapper, } from '../PlaceFormV2Screen';
import OptionsV2 from './OptionsV2';
import PhotosV2 from './PhotosV2';
import TextAreaV2 from './TextAreaV2';
export default function FloorMovementStep({ place, isStandaloneBuilding, onSubmit, onBack, onGuidePress, }) {
    var _a;
    const form = useFormContext();
    const isKeyboardVisible = useKeyboardVisible();
    const scrollViewRef = useRef(null);
    const handleTextAreaFocus = () => {
        if (Platform.OS !== 'ios')
            return;
        setTimeout(() => {
            var _a;
            (_a = scrollViewRef.current) === null || _a === void 0 ? void 0 : _a.scrollToEnd({ animated: true });
        }, 100);
    };
    // Watch floor movement methods
    const floorMovementMethods = (_a = form.watch('floorMovementMethod')) !== null && _a !== void 0 ? _a : [];
    const hasElevatorSelected = floorMovementMethods.includes(FloorMovingMethodTypeDto.PlaceElevator);
    const elevatorHasStairs = form.watch('elevatorHasStairs');
    const elevatorStairInfo = form.watch('elevatorStairInfo');
    // 엘리베이터 해제 시 서브필드 초기화
    useEffect(() => {
        if (!hasElevatorSelected) {
            form.setValue('elevatorPhotos', undefined);
            form.setValue('elevatorHasStairs', undefined);
            form.setValue('elevatorStairInfo', undefined);
            form.setValue('elevatorStairHeightLevel', undefined);
            form.setValue('elevatorHasSlope', undefined);
        }
    }, [hasElevatorSelected, form]);
    // Shared elevator conditions (stairInfo here maps to elevatorStairInfo)
    const elevatorConditions = useMemo(() => getElevatorConditions({
        hasElevator: true, // elevator section only renders when PlaceElevator is selected
        stairInfo: elevatorStairInfo,
    }), [elevatorStairInfo]);
    const noticeError = (errorKey) => {
        switch (errorKey) {
            case 'floorMovementMethod':
                ToastUtils.show('층간 이동 방법을 선택해주세요.', FORM_TOAST_OPTIONS);
                break;
            case 'elevatorPhotos':
                ToastUtils.show('엘리베이터 사진을 등록해주세요.', FORM_TOAST_OPTIONS);
                break;
            case 'elevatorHasStairs':
            case 'elevatorStairInfo':
            case 'elevatorStairHeightLevel':
                ToastUtils.show('계단 정보를 입력해주세요.', FORM_TOAST_OPTIONS);
                break;
            case 'elevatorHasSlope':
                ToastUtils.show('경사로 정보를 입력해주세요.', FORM_TOAST_OPTIONS);
                break;
        }
    };
    // 유효성 검사 및 첫 번째 에러 키 반환
    const validateAndGetError = () => {
        // 층간 이동 방법은 필수
        if (!floorMovementMethods || floorMovementMethods.length === 0) {
            return 'floorMovementMethod';
        }
        // 엘리베이터 선택 시 서브필드 검증
        if (hasElevatorSelected) {
            const photos = form.getValues('elevatorPhotos');
            if (!photos || photos.length === 0) {
                return 'elevatorPhotos';
            }
            const currentElevatorHasStairs = form.getValues('elevatorHasStairs');
            if (typeof currentElevatorHasStairs !== 'boolean') {
                return 'elevatorHasStairs';
            }
            if (currentElevatorHasStairs && !form.getValues('elevatorStairInfo')) {
                return 'elevatorStairInfo';
            }
            const currentStairInfo = form.getValues('elevatorStairInfo');
            if (currentElevatorHasStairs &&
                currentStairInfo === StairInfo.One &&
                !form.getValues('elevatorStairHeightLevel')) {
                return 'elevatorStairHeightLevel';
            }
            if (typeof form.getValues('elevatorHasSlope') !== 'boolean') {
                return 'elevatorHasSlope';
            }
        }
        return null;
    };
    // 등록 버튼 핸들러
    const handleSubmit = () => {
        const errorKey = validateAndGetError();
        if (errorKey) {
            noticeError(errorKey);
            return;
        }
        onSubmit();
    };
    return (_jsxs(_Fragment, { children: [_jsx(ScrollView, { ref: scrollViewRef, children: _jsxs(SafeAreaWrapper, { edges: ['bottom'], children: [_jsx(PlaceInfoSection, { target: "place", name: place.name, address: place.address, onGuidePress: onGuidePress }), _jsx(SectionSeparator, {}), _jsxs(FormContainer, { children: [_jsxs(SubSection, { children: [_jsxs(QuestionSection, { children: [_jsx(SectionLabel, { children: "\uCE35\uAC04 \uC774\uB3D9 \uC815\uBCF4" }), _jsx(QuestionText, { children: "1\uCE35\uC5D0\uC11C \uB2E4\uB978\uCE35\uC73C\uB85C \uC774\uB3D9\uAC00\uB2A5\uD55C \uBC29\uBC95\uC744 \uBAA8\uB450 \uC54C\uB824\uC8FC\uC138\uC694" })] }), _jsx(Controller, { name: "floorMovementMethod", rules: { required: true }, render: ({ field }) => {
                                                var _a;
                                                return (_jsx(OptionsV2.Multiple, { columns: 2, values: field.value, options: makeFloorMovementOptions(isStandaloneBuilding, (_a = field.value) !== null && _a !== void 0 ? _a : []), onSelect: field.onChange }));
                                            } })] }), hasElevatorSelected && (_jsxs(_Fragment, { children: [_jsxs(SubSection, { children: [_jsxs(View, { style: { gap: 2 }, children: [_jsxs(QuestionSection, { children: [_jsx(SectionLabel, { children: "\uC5D8\uB9AC\uBCA0\uC774\uD130 \uC815\uBCF4" }), _jsx(QuestionText, { children: "\uC5D8\uB9AC\uBCA0\uC774\uD130 \uC0AC\uC9C4\uC744 \uCC0D\uC5B4\uC8FC\uC138\uC694" })] }), _jsx(Hint, { children: "\uCD5C\uB300 3\uC7A5\uAE4C\uC9C0 \uB4F1\uB85D \uAC00\uB2A5\uD574\uC694" })] }), _jsx(Controller, { name: "elevatorPhotos", rules: { required: hasElevatorSelected }, render: ({ field }) => {
                                                        var _a;
                                                        return (_jsx(PhotosV2, { value: (_a = field.value) !== null && _a !== void 0 ? _a : [], onChange: field.onChange, target: "elevator", maxPhotos: MAX_NUMBER_OF_TAKEN_PHOTOS }));
                                                    } })] }), _jsxs(SubSection, { children: [_jsx(Label, { children: "\uC5D8\uB9AC\uBCA0\uC774\uD130\uAE4C\uC9C0 \uAC00\uB294 \uAE38\uC5D0 \uACC4\uB2E8\uC774 \uC788\uB098\uC694?" }), _jsxs(OptionsGroup, { children: [_jsx(Controller, { name: "elevatorHasStairs", rules: {
                                                                validate: hasElevatorSelected
                                                                    ? (v) => typeof v === 'boolean'
                                                                    : undefined,
                                                            }, render: ({ field }) => (_jsx(OptionsV2, { value: field.value, options: [
                                                                    { label: '있어요', value: true },
                                                                    { label: '없어요', value: false },
                                                                ], onSelect: field.onChange })) }), elevatorHasStairs && (_jsx(Controller, { name: "elevatorStairInfo", rules: {
                                                                required: hasElevatorSelected && elevatorHasStairs,
                                                            }, render: ({ field }) => (_jsx(OptionsV2, { value: field.value, columns: 3, options: [
                                                                    { label: '1칸', value: StairInfo.One },
                                                                    { label: '2-5칸', value: StairInfo.TwoToFive },
                                                                    { label: '6칸 이상', value: StairInfo.OverSix },
                                                                ], onSelect: field.onChange })) }))] })] }), elevatorHasStairs && elevatorConditions.showStairHeight && (_jsxs(SubSection, { children: [_jsx(Label, { children: "\uACC4\uB2E8 1\uCE78\uC758 \uB192\uC774\uB97C \uC54C\uB824\uC8FC\uC138\uC694" }), _jsx(MeasureGuide, { children: _jsx(Image, { source: formImages.stair, style: { width: '100%', height: '100%' } }) }), _jsx(View, { style: { gap: 16 }, children: _jsx(Controller, { name: "elevatorStairHeightLevel", rules: {
                                                            required: hasElevatorSelected &&
                                                                elevatorHasStairs &&
                                                                elevatorConditions.showStairHeight,
                                                        }, render: ({ field }) => (_jsx(OptionsV2, { value: field.value, options: ELEVATOR_OPTIONS.stairHeightOptions, onSelect: field.onChange })) }) })] }, "stair-height")), _jsxs(SubSection, { children: [_jsx(Label, { children: "\uC5D8\uB9AC\uBCA0\uC774\uD130\uAE4C\uC9C0 \uAC00\uB294 \uAE38\uC5D0 \uACBD\uC0AC\uB85C\uAC00 \uC788\uB098\uC694?" }), _jsx(Controller, { name: "elevatorHasSlope", rules: {
                                                        validate: hasElevatorSelected
                                                            ? (v) => typeof v === 'boolean'
                                                            : undefined,
                                                    }, render: ({ field }) => (_jsx(OptionsV2, { value: field.value, options: [
                                                            { label: '있어요', value: true },
                                                            { label: '없어요', value: false },
                                                        ], onSelect: field.onChange })) })] })] })), _jsxs(SubSection, { children: [_jsx(Label, { children: "\uB354 \uB3C4\uC6C0\uC774 \uB420 \uC815\uBCF4\uAC00 \uC788\uB2E4\uBA74 \uC54C\uB824\uC8FC\uC138\uC694" }), _jsx(Controller, { name: "floorMovementComment", render: ({ field }) => (_jsx(TextAreaV2, { placeholder: "\uC608\uC2DC) 1\uCE35\uC5D0 \uC774\uC6A9 \uACF5\uAC04\uC774 \uCDA9\uBD84\uD574\uC694", value: field.value, onChangeText: field.onChange, onFocus: handleTextAreaFocus })) })] })] })] }) }), _jsx(SafeAreaWrapper, { edges: isKeyboardVisible ? [] : ['bottom'], children: _jsxs(SubmitButtonWrapper, { children: [_jsx(SccButton, { text: "\uC774\uC804", onPress: onBack, buttonColor: "gray20", textColor: "gray90", fontFamily: font.pretendardSemibold, elementName: "place_form_v2_floor_movement_prev", style: { flex: 1, borderRadius: 12 } }), _jsx(SccButton, { text: "\uB4F1\uB85D\uD558\uAE30", onPress: handleSubmit, fontFamily: font.pretendardSemibold, buttonColor: "brandColor", elementName: "place_form_v2_floor_movement_next", style: { flex: 2, borderRadius: 12 } })] }) })] }));
}
const FormContainer = styled.View `
  background-color: white;
  padding-vertical: 40px;
  padding-horizontal: 20px;
  gap: 60px;
`;
